import React, { useState, useCallback } from 'react';
import { View, Text, Pressable, Platform, Alert, ScrollView, ActivityIndicator, TextInput } from 'react-native';
import { MaterialIcons, AntDesign } from '@expo/vector-icons';
import { YStack, XStack, Card, H4, SizableText, Separator, Dialog } from 'tamagui';
import { Image } from 'expo-image';
import styles, { getModernCardStyle, getModernActionStyle, getModernTextColor } from '@stylesheet';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import apis from '@api/api';

interface ImageEnhancementModalProps {
  visible: boolean;
  onClose: () => void;
  imageUrl: string;
  imageIndex?: number;
  onEnhancementComplete?: (enhancedImageUrl: string, imageIndex?: number) => void;
}

const ImageEnhancementModal: React.FC<ImageEnhancementModalProps> = ({
  visible,
  onClose,
  imageUrl,
  imageIndex,
  onEnhancementComplete
}) => {
  const theme = themeSelector();
  const [loading, setLoading] = useState(false);
  const [enhancedImage, setEnhancedImage] = useState<string | null>(null);
  const [enhancementStats, setEnhancementStats] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [enhancementPrompt, setEnhancementPrompt] = useState('');
  const [enhancementType, setEnhancementType] = useState<'upscale' | 'stylize' | 'remove-background'>('upscale');
  const [selectedStyle, setSelectedStyle] = useState<string>('');

  const handleEnhance = useCallback(async () => {
    if (!imageUrl) {
      Alert.alert('Error', 'No image provided');
      return;
    }

    setLoading(true);
    setError(null);
    setEnhancedImage(null);
    setEnhancementStats(null);

    try {
      // Call the enhancement API with enhancement type, style, and optional prompt
      const response = await apis.enhanceProductImage(
        imageUrl,
        undefined,
        enhancementType,
        2,
        enhancementPrompt.trim() || undefined,
        selectedStyle || undefined
      );

      if (response && response.success) {
        setEnhancedImage(response.enhancedImage);
        setEnhancementStats({
          method: response.method,
          originalSize: response.originalSize,
          enhancedSize: response.enhancedSize,
          sizeIncrease: response.sizeIncrease
        });
      } else {
        throw new Error(response?.error || response?.message || 'Enhancement failed');
      }
    } catch (err: any) {
      console.error('Image enhancement error:', err);
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to enhance image';
      setError(errorMessage);

      // Check if it's a rate limit error
      if (err?.response?.status === 429) {
        Alert.alert(
          'Rate Limit Exceeded',
          'You have reached your AI generation credit limit. Please upgrade your plan or try again later.',
          [{ text: 'OK' }]
        );
      } else {
        Alert.alert('Enhancement Failed', errorMessage);
      }
    } finally {
      setLoading(false);
    }
  }, [imageUrl, enhancementPrompt, enhancementType, selectedStyle]);

  const handleUseEnhanced = useCallback(() => {
    if (enhancedImage && onEnhancementComplete) {
      onEnhancementComplete(enhancedImage, imageIndex);
      onClose();
    }
  }, [enhancedImage, imageIndex, onEnhancementComplete, onClose]);

  const handleClose = useCallback(() => {
    setEnhancedImage(null);
    setEnhancementStats(null);
    setError(null);
    setEnhancementPrompt('');
    setEnhancementType('upscale');
    setSelectedStyle('');
    onClose();
  }, [onClose]);

  // Style presets for stylization
  const stylePresets = [
    { value: '', label: 'Default (Detail + Color Boost)', icon: 'auto-awesome' },
    { value: 'emboss', label: 'Emboss', icon: 'texture' },
    { value: 'detail', label: 'Enhanced Detail', icon: 'center-focus-strong' },
    { value: 'bright', label: 'Brighten', icon: 'wb-sunny' },
    { value: 'vibrant', label: 'Vibrant Colors', icon: 'palette' },
    { value: 'professional', label: 'Professional', icon: 'business-center' },
  ];

  return (
    <Dialog open={visible} onOpenChange={(open) => !open && handleClose()}>
      <Dialog.Portal>
        <Dialog.Overlay
          animation="quick"
          opacity={0.6}
          style={{
            zIndex: 1000,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            ...(Platform.OS === 'web' && {
              backdropFilter: 'blur(8px)',
            })
          }}
          enterStyle={{ opacity: 0 }}
          exitStyle={{ opacity: 0 }}
        />
        <Dialog.Content
          bordered
          elevate
          animation={[
            'quick',
            {
              opacity: {
                overshootClamping: true,
              },
            },
          ]}
          enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
          exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
          x={0}
          scale={1}
          style={[
            {
              padding: 0,
              minWidth: Platform.OS === 'web' ? 600 : undefined,
              maxWidth: Platform.OS === 'web' ? 800 : '90%',
              maxHeight: Platform.OS === 'web' ? '85%' : '90%',
              borderRadius: 24,
              overflow: 'hidden',
              elevation: 12,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 12 },
              shadowOpacity: 0.15,
              shadowRadius: 32,
              backgroundColor: theme ? 'rgba(255, 255, 255, 0.98)' : 'rgba(30, 41, 59, 0.98)',
              borderWidth: 1,
              borderColor: theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)',
            },
            Platform.OS === 'web' && {
              backdropFilter: 'blur(24px)',
              boxShadow: '0 12px 48px rgba(0,0,0,0.12)',
            }
          ]}
          opacity={1}
          y={0}
        >
          {/* Header */}
          <View style={{
            backgroundColor: theme ? '#f8fafc' : '#374151',
            paddingHorizontal: 24,
            paddingVertical: 20,
            borderBottomWidth: 1,
            borderBottomColor: theme ? '#e5e7eb' : '#4b5563',
          }}>
            <XStack alignItems="center" justifyContent="space-between">
              <XStack alignItems="center" gap="$3">
                <View style={{
                  backgroundColor: theme ? "#f0f9ff" : "#0c4a6e",
                  borderRadius: 12,
                  padding: 10,
                }}>
                  <MaterialIcons
                    name="auto-awesome"
                    size={20}
                    color={theme ? "#0ea5e9" : "#38bdf8"}
                  />
                </View>
                <YStack flex={1}>
                  <Dialog.Title style={{
                    fontSize: 20,
                    fontWeight: '700',
                    color: theme ? '#1e293b' : '#f1f5f9',
                    marginBottom: 4,
                    margin: 0,
                  }}>
                    AI Image Enhancement
                  </Dialog.Title>
                  <Dialog.Description style={{
                    fontSize: 14,
                    color: theme ? '#64748b' : '#94a3b8',
                    fontWeight: '500',
                    margin: 0,
                  }}>
                    Enhance your product images using AI
                  </Dialog.Description>
                </YStack>
              </XStack>
              <Pressable
                onPress={handleClose}
                style={{
                  padding: 8,
                  borderRadius: 8,
                  backgroundColor: theme ? '#F3F4F6' : '#4B5563'
                }}
              >
                <AntDesign name="close" size={20} color={theme ? '#1F2937' : '#F9FAFB'} />
              </Pressable>
            </XStack>
          </View>

          {/* Content */}
          <View style={{ padding: 24, backgroundColor: theme ? '#ffffff' : '#1e293b', flex: 1 }}>
            <YStack gap="$4" flex={1}>
              {/* Info Banner */}
              <Card padding="$3" backgroundColor={theme ? '#EFF6FF' : '#1E3A5F'} borderRadius={12}>
                <XStack alignItems="center" gap="$2">
                  <MaterialIcons name="info-outline" size={20} color={theme ? '#3B82F6' : '#93C5FD'} />
                  <SizableText size="$3" color={theme ? '#1E40AF' : '#DBEAFE'} flex={1}>
                    This feature uses AI generation credits. Choose an enhancement type to improve your image.
                  </SizableText>
                </XStack>
              </Card>

              {/* Enhancement Type Selector */}
              <YStack gap="$2">
                <SizableText
                  size="$4"
                  fontWeight="600"
                  color={theme ? '#1F2937' : '#F9FAFB'}
                >
                  Enhancement Type
                </SizableText>
                <XStack gap="$2" flexWrap="wrap">
                  {[
                    { value: 'upscale', label: 'Upscale', icon: 'zoom-in', description: 'Increase resolution' },
                    { value: 'stylize', label: 'Stylize', icon: 'palette', description: 'Apply artistic styles' },
                    { value: 'remove-background', label: 'Remove BG', icon: 'layers-clear', description: 'Remove background' },
                  ].map((type) => (
                    <Pressable
                      key={type.value}
                      onPress={() => {
                        setEnhancementType(type.value as any);
                        if (type.value !== 'stylize') {
                          setSelectedStyle('');
                        }
                      }}
                      style={{
                        flex: 1,
                        minWidth: 100,
                        padding: 16,
                        borderRadius: 12,
                        borderWidth: 2,
                        borderColor: enhancementType === type.value
                          ? 'rgb(90,192,234)'
                          : (theme ? '#e5e7eb' : '#4b5563'),
                        backgroundColor: enhancementType === type.value
                          ? (theme ? '#EFF6FF' : '#1E3A5F')
                          : (theme ? '#f8fafc' : '#374151'),
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      <MaterialIcons
                        name={type.icon as any}
                        size={24}
                        color={enhancementType === type.value
                          ? 'rgb(90,192,234)'
                          : (theme ? '#6b7280' : '#9ca3af')}
                      />
                      <Text style={{
                        color: enhancementType === type.value
                          ? 'rgb(90,192,234)'
                          : (theme ? '#1f2937' : '#f9fafb'),
                        fontWeight: enhancementType === type.value ? '600' : '500',
                        fontSize: 14,
                        textAlign: 'center',
                      }}>
                        {type.label}
                      </Text>
                      <Text style={{
                        color: theme ? '#6b7280' : '#9ca3af',
                        fontSize: 11,
                        textAlign: 'center',
                      }}>
                        {type.description}
                      </Text>
                    </Pressable>
                  ))}
                </XStack>
              </YStack>

              {/* Style Preset Selector (only for stylize) */}
              {enhancementType === 'stylize' && (
                <YStack gap="$2">
                  <SizableText
                    size="$4"
                    fontWeight="600"
                    color={theme ? '#1F2937' : '#F9FAFB'}
                  >
                    Style Preset
                  </SizableText>
                  <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                    <XStack gap="$2" paddingVertical="$1">
                      {stylePresets.map((preset) => (
                        <Pressable
                          key={preset.value}
                          onPress={() => setSelectedStyle(preset.value)}
                          style={{
                            paddingHorizontal: 16,
                            paddingVertical: 12,
                            borderRadius: 8,
                            borderWidth: 2,
                            borderColor: selectedStyle === preset.value
                              ? 'rgb(90,192,234)'
                              : (theme ? '#e5e7eb' : '#4b5563'),
                            backgroundColor: selectedStyle === preset.value
                              ? (theme ? '#EFF6FF' : '#1E3A5F')
                              : (theme ? '#f8fafc' : '#374151'),
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 8,
                            minWidth: 140,
                          }}
                        >
                          <MaterialIcons
                            name={preset.icon as any}
                            size={18}
                            color={selectedStyle === preset.value
                              ? 'rgb(90,192,234)'
                              : (theme ? '#6b7280' : '#9ca3af')}
                          />
                          <Text style={{
                            color: selectedStyle === preset.value
                              ? 'rgb(90,192,234)'
                              : (theme ? '#1f2937' : '#f9fafb'),
                            fontWeight: selectedStyle === preset.value ? '600' : '500',
                            fontSize: 12,
                            flex: 1,
                          }} numberOfLines={1}>
                            {preset.label}
                          </Text>
                        </Pressable>
                      ))}
                    </XStack>
                  </ScrollView>
                </YStack>
              )}

              {/* Enhancement Prompt Input */}
              <YStack gap="$2">
                <SizableText
                  size="$4"
                  fontWeight="600"
                  color={theme ? '#1F2937' : '#F9FAFB'}
                >
                  Enhancement Instructions (Optional)
                </SizableText>
                <TextInput
                  value={enhancementPrompt}
                  onChangeText={setEnhancementPrompt}
                  placeholder="e.g., 'Make colors more vibrant', 'Remove background', 'Increase sharpness', 'Add professional lighting'..."
                  placeholderTextColor={theme ? '#9ca3af' : '#6b7280'}
                  multiline
                  numberOfLines={3}
                  style={{
                    fontSize: 14,
                    padding: 12,
                    borderWidth: 1,
                    borderColor: theme ? '#e5e7eb' : '#4b5563',
                    borderRadius: 12,
                    backgroundColor: theme ? '#f8fafc' : '#374151',
                    color: theme ? '#1f2937' : '#f9fafb',
                    minHeight: 80,
                    textAlignVertical: 'top',
                  }}
                />
                <SizableText
                  size="$2"
                  color={getModernTextColor(theme, 'muted')}
                  style={{ fontStyle: 'italic' }}
                >
                  {enhancementType === 'upscale'
                    ? 'Leave empty for automatic upscaling, or describe specific improvements you want'
                    : enhancementType === 'stylize'
                      ? 'Describe the artistic style or effect you want (optional, style preset will be applied)'
                      : 'Describe any specific requirements for background removal (optional)'}
                </SizableText>
              </YStack>

              <ScrollView style={{ flex: 1 }} contentContainerStyle={{ gap: 16, paddingBottom: 16 }}>
                {/* Original Image */}
                <Card padding="$4" backgroundColor={theme ? '#F9FAFB' : '#1F2937'} borderRadius={12}>
                  <YStack gap="$3">
                    <SizableText size="$4" fontWeight="600" color={theme ? '#1F2937' : '#F9FAFB'}>
                      Original Image
                    </SizableText>
                    <View style={{
                      width: '100%',
                      height: 300,
                      borderRadius: 12,
                      overflow: 'hidden',
                      backgroundColor: theme ? '#E5E7EB' : '#374151',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}>
                      <Image
                        source={{ uri: imageUrl }}
                        style={{ width: '100%', height: '100%' }}
                        contentFit="contain"
                      />
                    </View>
                  </YStack>
                </Card>

                {/* Enhanced Image */}
                {enhancedImage && (
                  <Card padding="$4" backgroundColor={theme ? '#F0FDF4' : '#1F2937'} borderRadius={12}>
                    <YStack gap="$3">
                      <XStack alignItems="center" gap="$2">
                        <MaterialIcons name="auto-awesome" size={20} color="#10B981" />
                        <SizableText size="$4" fontWeight="600" color={theme ? '#1F2937' : '#F9FAFB'}>
                          Enhanced Image
                        </SizableText>
                      </XStack>
                      <View style={{
                        width: '100%',
                        height: 300,
                        borderRadius: 12,
                        overflow: 'hidden',
                        backgroundColor: theme ? '#E5E7EB' : '#374151',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}>
                        <Image
                          source={{ uri: enhancedImage }}
                          style={{ width: '100%', height: '100%' }}
                          contentFit="contain"
                        />
                      </View>
                      {enhancementStats && (
                        <XStack gap="$3" flexWrap="wrap">
                          <Card padding="$2" backgroundColor={theme ? '#ffffff' : '#374151'} borderRadius={8}>
                            <SizableText size="$2" color={getModernTextColor(theme, 'muted')}>Method</SizableText>
                            <SizableText size="$3" fontWeight="600">{enhancementStats.method}</SizableText>
                          </Card>
                          <Card padding="$2" backgroundColor={theme ? '#ffffff' : '#374151'} borderRadius={8}>
                            <SizableText size="$2" color={getModernTextColor(theme, 'muted')}>Size Increase</SizableText>
                            <SizableText size="$3" fontWeight="600" color="#10B981">{enhancementStats.sizeIncrease}</SizableText>
                          </Card>
                        </XStack>
                      )}
                    </YStack>
                  </Card>
                )}

                {/* Error Display */}
                {error && (
                  <Card padding="$4" backgroundColor={theme ? '#FEF2F2' : '#7F1D1D'} borderRadius={12}>
                    <XStack alignItems="center" gap="$2">
                      <MaterialIcons name="error-outline" size={20} color="#EF4444" />
                      <SizableText size="$3" color={theme ? '#991B1B' : '#FCA5A5'} flex={1}>
                        {error}
                      </SizableText>
                    </XStack>
                  </Card>
                )}
              </ScrollView>

              {/* Action Buttons */}
              <XStack gap="$3" justifyContent="flex-end" borderTopWidth={1} borderTopColor={theme ? '#e5e7eb' : '#4b5563'} paddingTop="$4" marginTop="$2">
                <Dialog.Close asChild>
                  <Pressable
                    onPress={handleClose}
                    style={{
                      paddingHorizontal: 20,
                      paddingVertical: 12,
                      borderRadius: 12,
                      backgroundColor: theme ? '#f1f5f9' : '#4b5563',
                      borderWidth: 1,
                      borderColor: theme ? '#e2e8f0' : '#6b7280',
                      minWidth: 100,
                      alignItems: 'center',
                    }}
                  >
                    <Text style={{
                      color: theme ? '#64748b' : '#d1d5db',
                      fontWeight: '600',
                      fontSize: 14,
                    }}>
                      Cancel
                    </Text>
                  </Pressable>
                </Dialog.Close>

                {!enhancedImage && (
                  <Pressable
                    onPress={handleEnhance}
                    disabled={loading}
                    style={{
                      paddingHorizontal: 20,
                      paddingVertical: 12,
                      borderRadius: 12,
                      backgroundColor: theme ? '#3b82f6' : '#2563eb',
                      minWidth: 150,
                      alignItems: 'center',
                      flexDirection: 'row',
                      gap: 8,
                      opacity: loading ? 0.6 : 1,
                      elevation: 2,
                      shadowColor: '#3b82f6',
                      shadowOffset: { width: 0, height: 2 },
                      shadowOpacity: 0.2,
                      shadowRadius: 8,
                    }}
                  >
                    {loading ? (
                      <>
                        <ActivityIndicator size="small" color="white" />
                        <Text style={{
                          color: '#ffffff',
                          fontWeight: '600',
                          fontSize: 14,
                        }}>
                          Enhancing...
                        </Text>
                      </>
                    ) : (
                      <>
                        <MaterialIcons
                          name={
                            enhancementType === 'upscale' ? 'zoom-in' :
                              enhancementType === 'stylize' ? 'palette' :
                                'layers-clear'
                          }
                          size={18}
                          color="white"
                        />
                        <Text style={{
                          color: '#ffffff',
                          fontWeight: '600',
                          fontSize: 14,
                        }}>
                          {enhancementType === 'upscale' ? 'Upscale Image' :
                            enhancementType === 'stylize' ? 'Stylize Image' :
                              'Remove Background'}
                        </Text>
                      </>
                    )}
                  </Pressable>
                )}

                {enhancedImage && (
                  <Pressable
                    onPress={handleUseEnhanced}
                    style={{
                      paddingHorizontal: 20,
                      paddingVertical: 12,
                      borderRadius: 12,
                      backgroundColor: theme ? '#10b981' : '#059669',
                      minWidth: 150,
                      alignItems: 'center',
                      flexDirection: 'row',
                      gap: 8,
                      elevation: 2,
                      shadowColor: '#10b981',
                      shadowOffset: { width: 0, height: 2 },
                      shadowOpacity: 0.2,
                      shadowRadius: 8,
                    }}
                  >
                    <MaterialIcons name="check-circle" size={18} color="white" />
                    <Text style={{
                      color: '#ffffff',
                      fontWeight: '600',
                      fontSize: 14,
                    }}>
                      Use Enhanced Image
                    </Text>
                  </Pressable>
                )}
              </XStack>
            </YStack>
          </View>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
};

export default ImageEnhancementModal;

